import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Breadcrumb from '../../../shared/ui/Breadcrumb';
import LoadingSpinner from '../../../shared/ui/LoadingSpinner';
import LoginPrompt from '../../../shared/ui/LoginPrompt';
import ToggleSwitch from '../../../shared/ui/ToggleSwitch';
import {useAuth} from '../../../shared/context/AuthContext';
import {
  checkUserPowerAtGuild,
  createRanking,
  deleteRanking,
  fetchGuildRankings,
  fetchMutualGuilds
} from '../../../services/api';
import './GuildRankingsPage.css';

export default function GuildRankingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guildId = searchParams.get('guildId');
  const {isAuthenticated, loading: authLoading} = useAuth();

  const [rankings, setRankings] = useState([]);
  const [guildName, setGuildName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newRankingName, setNewRankingName] = useState('');
  const [mutationError, setMutationError] = useState('');
  const [isCreatingRanking, setIsCreatingRanking] = useState(false);
  const [pendingDeleteName, setPendingDeleteName] = useState('');
  const [deletingRankingName, setDeletingRankingName] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasGuildPower, setHasGuildPower] = useState(false);
  const guildPowerCacheRef = useRef({});

  const headingGuildName = useMemo(() => {
    if (guildName) {
      return guildName;
    }
    return 'Server';
  }, [guildName]);

  const breadcrumbItems = useMemo(() => {
    const items = [{label: 'Ranky', path: '/'}];
    if (guildName) {
      items.push({label: guildName, isCurrent: true});
    }
    return items;
  }, [guildName]);

  const handleBreadcrumbBack = () => {
    navigate('/');
  };

  const handleRankingClick = (rankingName) => {
    navigate(
        `/guilds/${encodeURIComponent(guildId)}/rankings/${encodeURIComponent(
            rankingName)}`,
        {
          state: {guildName}
        });
  };

  const handleCreateRanking = async (event) => {
    event.preventDefault();

    if (!guildId || isCreatingRanking || !isEditMode || !hasGuildPower) {
      return;
    }

    const normalizedName = newRankingName.trim();
    if (!normalizedName) {
      setMutationError('Ranking name cannot be empty.');
      return;
    }

    const alreadyExists = rankings.some(
        ranking => ranking?.name?.toLowerCase()
            === normalizedName.toLowerCase());
    if (alreadyExists) {
      setMutationError('A ranking with this name already exists.');
      return;
    }

    setMutationError('');
    setIsCreatingRanking(true);

    try {
      const createdRanking = await createRanking(guildId, normalizedName);
      const rankingCard = createdRanking && typeof createdRanking === 'object'
          ? createdRanking
          : {name: normalizedName, amountOfAccounts: 0};
      setRankings(previousRankings => [...previousRankings, rankingCard]);
      setNewRankingName('');
    } catch (err) {
      setMutationError(
          err?.message || 'Error creating ranking for this server.');
    } finally {
      setIsCreatingRanking(false);
    }
  };

  const handleDeleteIntent = (rankingName) => {
    if (deletingRankingName || !isEditMode || !hasGuildPower) {
      return;
    }

    setMutationError('');
    setPendingDeleteName(currentDeleteName =>
        currentDeleteName === rankingName ? '' : rankingName
    );
  };

  const handleDeleteRanking = async (rankingName) => {
    if (!guildId || deletingRankingName || !isEditMode || !hasGuildPower) {
      return;
    }

    const rankingIndex = rankings.findIndex(
        ranking => ranking.name === rankingName);
    const rankingSnapshot = rankings[rankingIndex];

    setMutationError('');
    setPendingDeleteName('');
    setDeletingRankingName(rankingName);
    setRankings(previousRankings =>
        previousRankings.filter(ranking => ranking.name !== rankingName)
    );

    try {
      await deleteRanking(guildId, rankingName);
    } catch (err) {
      // Restore removed card if optimistic delete fails.
      setRankings(previousRankings => {
        if (!rankingSnapshot || previousRankings.some(
            ranking => ranking.name === rankingName)) {
          return previousRankings;
        }

        const restoredRankings = [...previousRankings];
        const restoredIndex = Math.max(0,
            Math.min(rankingIndex, restoredRankings.length));
        restoredRankings.splice(restoredIndex, 0, rankingSnapshot);
        return restoredRankings;
      });
      setMutationError(
          err?.message || 'Error deleting ranking from this server.');
    } finally {
      setDeletingRankingName('');
    }
  };

  const handleEditModeToggle = () => {
    setIsEditMode(previousMode => {
      const nextMode = !previousMode;

      if (!nextMode) {
        setMutationError('');
        setNewRankingName('');
        setPendingDeleteName('');
      }

      return nextMode;
    });
  };

  useEffect(() => {
    if (!guildId) {
      setHasGuildPower(false);
      return;
    }

    if (isEditMode) {
      return;
    }

    const cachedGuildPower = guildPowerCacheRef.current[guildId];
    if (typeof cachedGuildPower === 'boolean') {
      setHasGuildPower(cachedGuildPower);
      return;
    }

    let isCancelled = false;

    const checkGuildPower = async () => {
      try {
        const hasPowerResult = await checkUserPowerAtGuild(guildId);
        if (isCancelled) {
          return;
        }

        const hasPower = Boolean(hasPowerResult);
        guildPowerCacheRef.current[guildId] = hasPower;
        setHasGuildPower(hasPower);
      } catch (err) {
        if (!isCancelled) {
          guildPowerCacheRef.current[guildId] = false;
          setHasGuildPower(false);
        }
      }
    };

    checkGuildPower();

    return () => {
      isCancelled = true;
    };
  }, [guildId, isEditMode]);

  useEffect(() => {
    if (!guildId) {
      setRankings([]);
      setGuildName('');
      setError('');
      setNewRankingName('');
      setMutationError('');
      setPendingDeleteName('');
      setDeletingRankingName('');
      setIsEditMode(false);
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const loadGuildData = async () => {
      setLoading(true);
      setError('');
      setNewRankingName('');
      setMutationError('');
      setPendingDeleteName('');
      setDeletingRankingName('');
      setIsEditMode(false);

      try {
        const guildRankings = await fetchGuildRankings(guildId);

        setRankings(Array.isArray(guildRankings) ? guildRankings : []);

        try {
          const mutualGuilds = await fetchMutualGuilds();
          const selectedGuild = Array.isArray(mutualGuilds)
              ? mutualGuilds.find(guild => guild.id === guildId)
              : null;
          setGuildName(selectedGuild?.name || '');
        } catch (guildError) {
          setGuildName('');
        }
      } catch (err) {
        setRankings([]);
        setError(err?.message || 'Error loading rankings for this server.');
      } finally {
        setLoading(false);
      }
    };

    loadGuildData();
  }, [guildId, isAuthenticated]);

  if (!guildId) {
    return (
        <div className="guild-rankings-page guild-rankings-state">
          Select a server from the left sidebar to view its rankings.
        </div>
    );
  }

  if (authLoading || (loading && isAuthenticated)) {
    return (
        <section className="guild-rankings-page">
          <div className="guild-rankings-breadcrumb-nav">
            <button
                className="guild-rankings-back-arrow"
                onClick={handleBreadcrumbBack}
                title="Back to home"
                aria-label="Back to home"
            >
              ‹
            </button>
            <Breadcrumb items={breadcrumbItems}/>
          </div>
          <header className="guild-rankings-header">
          </header>
          <div className="guild-rankings-loading">
            <LoadingSpinner message="Loading rankings..."/>
          </div>
        </section>
    );
  }

  if (!isAuthenticated) {
    return (
        <section className="guild-rankings-page">
          <div className="guild-rankings-breadcrumb-nav">
            <button
                className="guild-rankings-back-arrow"
                onClick={handleBreadcrumbBack}
                title="Back to home"
                aria-label="Back to home"
            >
              ‹
            </button>
            <Breadcrumb items={breadcrumbItems}/>
          </div>
          <LoginPrompt
              message="You need to be logged in to view this server's rankings."/>
        </section>
    );
  }

  return (
      <section className="guild-rankings-page">
        {hasGuildPower && (
            <div className="guild-rankings-edit-toggle-anchor">
              <ToggleSwitch
                  label="Edit mode"
                  checked={isEditMode}
                  onChange={handleEditModeToggle}
              />
            </div>
        )}

        <div className="guild-rankings-breadcrumb-nav">
          <button
              className="guild-rankings-back-arrow"
              onClick={handleBreadcrumbBack}
              title="Back to home"
              aria-label="Back to home"
          >
            ‹
          </button>
          <Breadcrumb items={breadcrumbItems}/>
        </div>
        <header className="guild-rankings-header">
          <h1>{headingGuildName} rankings</h1>
          <p>
            Browse all available rankings configured for this Discord server.
          </p>
        </header>

        {isEditMode && hasGuildPower && (
            <section className="guild-rankings-edit-panel"
                     aria-label="Ranking editing controls">
              <form className="guild-rankings-create-form"
                    onSubmit={handleCreateRanking}>
                <input
                    type="text"
                    value={newRankingName}
                    onChange={(event) => setNewRankingName(event.target.value)}
                    className="guild-rankings-create-input"
                    placeholder="New ranking name"
                    maxLength={50}
                    disabled={isCreatingRanking || Boolean(deletingRankingName)}
                    aria-label="New ranking name"
                />
                <button
                    type="submit"
                    className="guild-rankings-create-button"
                    disabled={isCreatingRanking || Boolean(deletingRankingName)}
                >
                  {isCreatingRanking ? 'Creating...' : 'Create ranking'}
                </button>
              </form>
            </section>
        )}

        {error && (
            <p className="guild-rankings-state guild-rankings-error">{error}</p>
        )}

        {!error && isEditMode && mutationError && (
            <p className="guild-rankings-state guild-rankings-error">{mutationError}</p>
        )}

        {!error && rankings.length === 0 && (
            <p className="guild-rankings-state">
              No rankings found for this server yet.
            </p>
        )}

        {!error && rankings.length > 0 && (
            <div className="guild-rankings-grid">
              {rankings.map((ranking, index) => (
                  <article
                      key={`${ranking.name}-${index}`}
                      className="guild-ranking-card"
                  >
                    <button
                        type="button"
                        className="guild-ranking-card-main"
                        onClick={() => handleRankingClick(ranking.name)}
                    >
                      <h2>{ranking.name}</h2>
                      <p>
                        {ranking.amountOfAccounts || 0} tracked accounts
                      </p>
                    </button>

                    {isEditMode && hasGuildPower && (
                        <div className="guild-ranking-card-actions">
                          {pendingDeleteName === ranking.name
                              ? (
                                  <>
                                    <button
                                        type="button"
                                        className="guild-ranking-confirm-delete-button"
                                        onClick={() => handleDeleteRanking(
                                            ranking.name)}
                                        disabled={Boolean(deletingRankingName)}
                                    >
                                      Confirm delete
                                    </button>
                                    <button
                                        type="button"
                                        className="guild-ranking-cancel-delete-button"
                                        onClick={() => setPendingDeleteName('')}
                                        disabled={Boolean(deletingRankingName)}
                                    >
                                      Cancel
                                    </button>
                                  </>
                              )
                              : (
                                  <button
                                      type="button"
                                      className="guild-ranking-delete-button"
                                      onClick={() => handleDeleteIntent(
                                          ranking.name)}
                                      disabled={Boolean(deletingRankingName)}
                                  >
                                    Delete
                                  </button>
                              )}
                        </div>
                    )}
                  </article>
              ))}
            </div>
        )}
      </section>
  );
}

